import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Calendar, ArrowLeft } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';

const CampaignDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    loadCampaign();
  }, [slug]);

  // Update title when campaign loads
  useEffect(() => {
    if (campaign) {
      document.title = `${campaign.title} - ${settings.site_name || 'Cinefora'}`;
    }
  }, [campaign, settings.site_name]);

  const loadCampaign = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/campaigns/slug/${slug}`);
      setCampaign(response.data);
    } catch (error) {
      console.error('Campaign load error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black">
        <Header />
        <div className="container mx-auto px-4 py-32">
          <div className="text-white text-center">Yükleniyor...</div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="min-h-screen bg-black">
        <Header />
        <div className="container mx-auto px-4 py-32">
          <div className="text-white text-center">Kampanya bulunamadı</div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <Header />
      
      <div className="pt-32 pb-16">
        <div className="container mx-auto px-4">
          {/* Back Button */}
          <button
            onClick={() => navigate('/kampanyalar')}
            className="flex items-center text-[#FDB714] hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" />
            Kampanyalara Dön
          </button>

          {/* Hero Image */}
          <div className="relative w-full h-[400px] rounded-lg overflow-hidden mb-8">
            <img
              src={campaign.image}
              alt={campaign.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
          </div>

          {/* Content */}
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {campaign.title}
            </h1>

            <div className="flex items-center gap-4 text-gray-400 mb-8">
              <div className="flex items-center gap-2">
                <Calendar size={18} />
                <span>{campaign.start_date} - {campaign.end_date}</span>
              </div>
            </div>

            {/* Short Description */}
            <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-xl p-6 text-gray-300 text-lg mb-8 leading-relaxed">
              {campaign.description}
            </div>

            {/* Rich Content */}
            {campaign.content && (
              <div 
                className="prose prose-invert prose-lg max-w-none text-white bg-gradient-to-br from-purple-500/5 to-pink-500/5 border border-purple-500/10 rounded-xl p-8"
                dangerouslySetInnerHTML={{ __html: campaign.content }}
                style={{
                  fontSize: '1.125rem',
                  lineHeight: '1.75',
                }}
              />
            )}
          </div>
        </div>
      </div>

      <Footer />
      
      <style jsx>{`
        .prose h1, .prose h2, .prose h3, .prose h4, .prose h5, .prose h6 {
          background: linear-gradient(135deg, #a855f7 0%, #ec4899 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin-top: 2rem;
          margin-bottom: 1rem;
          font-weight: 700;
        }
        .prose p {
          margin-bottom: 1rem;
          color: #e5e7eb;
        }
        .prose ul, .prose ol {
          margin-left: 1.5rem;
          margin-bottom: 1rem;
        }
        .prose li {
          margin-bottom: 0.5rem;
        }
        .prose img {
          border-radius: 0.5rem;
          margin: 2rem 0;
          border: 1px solid rgba(168, 85, 247, 0.2);
        }
        .prose a {
          background: linear-gradient(135deg, #a855f7 0%, #ec4899 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          text-decoration: underline;
          text-decoration-color: rgba(168, 85, 247, 0.5);
        }
        .prose a:hover {
          text-decoration-color: #ec4899;
        }
      `}</style>
    </div>
  );
};

export default CampaignDetail;
